import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import AnimatedCursor from 'react-animated-cursor';

const CLICKABLE_SELECTOR = 'a, button, [role="button"], input, select, textarea, label';

// Must stay in sync with the `cursor: none` rule in index.css — if the CSS hid the native
// pointer at a width where this component no longer renders, that viewport would have no
// cursor at all. 768px is the same breakpoint ScrollToTopButton treats as mobile.
//
// The pointer checks matter separately from the width: a phone held in landscape is wide
// enough to pass min-width, and a dot that trails a finger by design is worse than useless
// on a touchscreen.
const CURSOR_MEDIA_QUERY = '(hover: hover) and (pointer: fine) and (min-width: 768px)';

// Drives the `cursor: none` rule in index.css. The library only hides the native pointer
// on <body> and on the clickables it finds at mount, and re-scans only when its hover
// state happens to change — so a link rendered by a later route keeps the UA stylesheet's
// hand. Mounted once for the whole app, that gap spans every navigation, so the hiding is
// done in CSS instead of per-element.
const CURSOR_CLASS = 'has-custom-cursor';

// The library stacks its cursors at z-index 999, which loses to the fixed chrome on this
// site (ScrollToTopButton is 9999).
const LAYER = 10000;

// The inner dot is sized away so this reads as a single disc.
const INNER_STYLE = { zIndex: LAYER, backgroundColor: 'transparent' };

// No backgroundColor in either style object — outerStyle merges last and would clobber
// the rgba(color, outerAlpha) that drives the fill.
const OUTER_STYLE = {
    zIndex: LAYER,
    border: '1px solid rgba(255, 255, 255, 0.55)'
};

// On hover the fill drops out and the edge thickens, so the grown circle is a ring rather
// than a disc. A solid 44px disc would sit on top of whatever you were aiming at — it
// fully covers the 24–32px contact icons.
const OUTER_STYLE_HOVER = {
    zIndex: LAYER,
    border: '2px solid rgba(255, 255, 255, 0.95)',
    // Keeps the ring legible where it crosses a pale part of a background photo.
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.18)'
};

// EVERY object/array here is a module constant on purpose. react-animated-cursor
// re-renders on each mousemove and rebuilds its options from these props by identity, so
// an inline literal makes it re-attach five listeners to every link on every frame — and
// its cleanup passes new function refs to removeEventListener, so none of them ever come
// off. That pile-up starves the rAF loop that positions the circle, and the cursor
// vanishes.
const CLICKABLES = [
    { target: CLICKABLE_SELECTOR, outerAlpha: 0, outerStyle: OUTER_STYLE_HOVER }
];

/**
 * A translucent white dot that replaces the pointer, opening into an outlined ring over
 * anything clickable. Wrapper over react-animated-cursor, which handles pointer tracking,
 * the trailing motion, and hiding the system cursor.
 *
 * Mounted once in App, above the routes, so it isn't torn down and rebuilt on every
 * navigation. Renders nothing at mobile sizes or on touch input — see CURSOR_MEDIA_QUERY.
 *
 * Portalled to <body> because the cursor is positioned `fixed` and a transformed ancestor
 * becomes the containing block for its fixed descendants — PageTransition's
 * `.animate-fadeIn` ends on `transform: translateY(0)` with `forwards`, so anywhere
 * inside it the cursor would scroll off with the document.
 */
export default function CustomCursor() {
    // Tracked in state rather than read once, so resizing a desktop window down past the
    // breakpoint drops the cursor immediately instead of at the next reload.
    const [enabled, setEnabled] = useState(
        () => window.matchMedia(CURSOR_MEDIA_QUERY).matches
    );

    useEffect(() => {
        const query = window.matchMedia(CURSOR_MEDIA_QUERY);
        const onChange = (event) => setEnabled(event.matches);

        // Re-read on mount: the viewport can change between the lazy initializer and here.
        setEnabled(query.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;

        document.body.classList.add(CURSOR_CLASS);

        // The library hides the native cursor but never puts it back — not on body, and
        // not on the clickables it marks inline. This also runs when `enabled` flips
        // false, which is what hands the system pointer back on a resize down to mobile.
        return () => {
            document.body.classList.remove(CURSOR_CLASS);
            document.body.style.cursor = '';
            document.querySelectorAll(CLICKABLE_SELECTOR).forEach((el) => {
                el.style.cursor = '';
            });
        };
    }, [enabled]);

    if (!enabled) return null;

    return createPortal(
        <AnimatedCursor
            color="255,255,255"
            showSystemCursor={false}
            trailingSpeed={4}
            innerSize={0}
            innerScale={0}
            innerStyle={INNER_STYLE}
            outerSize={26}
            outerAlpha={0.5}
            outerScale={1.7}
            outerStyle={OUTER_STYLE}
            clickables={CLICKABLES}
        />,
        document.body
    );
}
